import { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, Check, AlertCircle } from "lucide-react";

// Common country codes with phone validation patterns
const COUNTRIES = [
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧", pattern: /^(\+44|0)?[1-9]\d{9,10}$/, placeholder: "7123 456789" },
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸", pattern: /^(\+1)?[2-9]\d{9}$/, placeholder: "555 123 4567" },
  { code: "IN", name: "India", dial: "+91", flag: "🇮🇳", pattern: /^(\+91)?[6-9]\d{9}$/, placeholder: "98765 43210" },
  { code: "DE", name: "Germany", dial: "+49", flag: "🇩🇪", pattern: /^(\+49|0)?[1-9]\d{6,14}$/, placeholder: "151 1234567" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷", pattern: /^(\+33|0)?[1-9]\d{8}$/, placeholder: "6 12 34 56 78" },
  { code: "ES", name: "Spain", dial: "+34", flag: "🇪🇸", pattern: /^(\+34)?[6-9]\d{8}$/, placeholder: "612 345 678" },
  { code: "IT", name: "Italy", dial: "+39", flag: "🇮🇹", pattern: /^(\+39)?[0-9]{9,11}$/, placeholder: "312 345 6789" },
  { code: "NL", name: "Netherlands", dial: "+31", flag: "🇳🇱", pattern: /^(\+31|0)?[1-9]\d{8}$/, placeholder: "6 12345678" },
  { code: "BE", name: "Belgium", dial: "+32", flag: "🇧🇪", pattern: /^(\+32|0)?[1-9]\d{7,8}$/, placeholder: "470 12 34 56" },
  { code: "AU", name: "Australia", dial: "+61", flag: "🇦🇺", pattern: /^(\+61|0)?[2-9]\d{8}$/, placeholder: "412 345 678" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦", pattern: /^(\+1)?[2-9]\d{9}$/, placeholder: "416 123 4567" },
  { code: "AE", name: "UAE", dial: "+971", flag: "🇦🇪", pattern: /^(\+971|0)?[1-9]\d{7,8}$/, placeholder: "50 123 4567" },
  { code: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬", pattern: /^(\+65)?[689]\d{7}$/, placeholder: "9123 4567" },
  { code: "IE", name: "Ireland", dial: "+353", flag: "🇮🇪", pattern: /^(\+353|0)?[1-9]\d{7,9}$/, placeholder: "85 123 4567" },
  { code: "PK", name: "Pakistan", dial: "+92", flag: "🇵🇰", pattern: /^(\+92|0)?[1-9]\d{9}$/, placeholder: "300 1234567" },
  { code: "BD", name: "Bangladesh", dial: "+880", flag: "🇧🇩", pattern: /^(\+880|0)?[1-9]\d{9}$/, placeholder: "1712 345678" },
  { code: "PH", name: "Philippines", dial: "+63", flag: "🇵🇭", pattern: /^(\+63|0)?[9]\d{9}$/, placeholder: "917 123 4567" },
  { code: "NZ", name: "New Zealand", dial: "+64", flag: "🇳🇿", pattern: /^(\+64|0)?[2-9]\d{7,9}$/, placeholder: "21 123 4567" },
  { code: "ZA", name: "South Africa", dial: "+27", flag: "🇿🇦", pattern: /^(\+27|0)?[1-9]\d{8}$/, placeholder: "71 123 4567" },
  { code: "BR", name: "Brazil", dial: "+55", flag: "🇧🇷", pattern: /^(\+55)?[1-9]{2}[9]?\d{8}$/, placeholder: "11 91234 5678" },
  { code: "MX", name: "Mexico", dial: "+52", flag: "🇲🇽", pattern: /^(\+52)?[1-9]\d{9}$/, placeholder: "55 1234 5678" },
  { code: "JP", name: "Japan", dial: "+81", flag: "🇯🇵", pattern: /^(\+81|0)?[1-9]\d{8,9}$/, placeholder: "90 1234 5678" },
  { code: "KR", name: "South Korea", dial: "+82", flag: "🇰🇷", pattern: /^(\+82|0)?[1-9]\d{8,9}$/, placeholder: "10 1234 5678" },
  { code: "CN", name: "China", dial: "+86", flag: "🇨🇳", pattern: /^(\+86)?1[3-9]\d{9}$/, placeholder: "138 1234 5678" },
];

// Helper to parse initial value
const parseInitialValue = (value) => {
  if (!value) return { country: COUNTRIES[0], number: "" };
  
  const matchedCountry = COUNTRIES.find(c => value.startsWith(c.dial));
  if (matchedCountry) {
    return { 
      country: matchedCountry, 
      number: value.replace(matchedCountry.dial, "").trim() 
    };
  }
  return { country: COUNTRIES[0], number: value.replace(/^\+\d+\s*/, "") };
};

export default function PhoneInput({ 
  value = "", 
  onChange, 
  onValidation,
  placeholder,
  className = "",
  autoDetectCountry = true
}) {
  const initialParsed = parseInitialValue(value);
  const [selectedCountry, setSelectedCountry] = useState(initialParsed.country);
  const [phoneNumber, setPhoneNumber] = useState(initialParsed.number);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isValid, setIsValid] = useState(null);
  const detectedRef = useRef(false);

  // Auto-detect country from IP on mount (only once)
  useEffect(() => {
    if (autoDetectCountry && !value && !detectedRef.current) {
      detectedRef.current = true;
      fetch("https://ipapi.co/json/")
        .then(res => res.json())
        .then(data => {
          if (data.country_code) {
            const country = COUNTRIES.find(c => c.code === data.country_code);
            if (country) {
              setSelectedCountry(country);
            }
          }
        })
        .catch(() => {/* Keep default */});
    }
  }, [autoDetectCountry, value]);

  // Handle phone number change
  const handlePhoneChange = useCallback((newNumber) => {
    setPhoneNumber(newNumber);
    
    const fullNumber = newNumber ? `${selectedCountry.dial} ${newNumber}`.trim() : "";
    
    if (newNumber) {
      const cleanNumber = newNumber.replace(/[\s\-\(\)]/g, "");
      const fullClean = `${selectedCountry.dial}${cleanNumber}`;
      const valid = selectedCountry.pattern.test(fullClean) || selectedCountry.pattern.test(cleanNumber);
      setIsValid(valid);
      onValidation?.(valid);
    } else {
      setIsValid(null);
      onValidation?.(true);
    }
    
    onChange?.(fullNumber);
  }, [selectedCountry, onChange, onValidation]);

  // Handle country change
  const handleCountryChange = useCallback((country) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    setSearchTerm("");
    
    // Re-validate with new country
    if (phoneNumber) {
      const fullNumber = `${country.dial} ${phoneNumber}`.trim();
      const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, "");
      const fullClean = `${country.dial}${cleanNumber}`;
      const valid = country.pattern.test(fullClean) || country.pattern.test(cleanNumber);
      setIsValid(valid);
      onValidation?.(valid);
      onChange?.(fullNumber);
    }
  }, [phoneNumber, onChange, onValidation]);

  const filteredCountries = COUNTRIES.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.dial.includes(searchTerm) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`relative ${className}`}>
      <div className="flex">
        {/* Country Selector */}
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-1 px-2 py-2 border border-r-0 rounded-l-md bg-gray-50 hover:bg-gray-100 transition-colors shrink-0"
        >
          <span className="text-base">{selectedCountry.flag}</span>
          <span className="text-xs font-medium text-gray-600">{selectedCountry.dial}</span>
          <ChevronDown className="w-3 h-3 text-gray-400" />
        </button>
        
        {/* Phone Number Input */}
        <div className="relative flex-1 min-w-0">
          <Input
            type="tel"
            value={phoneNumber}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder={placeholder || selectedCountry.placeholder}
            className={`rounded-l-none ${isValid === false ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
          />
          {/* Validation Indicator */}
          {isValid !== null && phoneNumber && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isValid ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <AlertCircle className="w-4 h-4 text-red-500" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Validation Message */}
      {isValid === false && phoneNumber && (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Please enter a valid {selectedCountry.name} phone number
        </p>
      )}

      {/* Country Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b sticky top-0 bg-white">
            <Input
              type="text"
              placeholder="Search country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-sm"
              autoFocus
            />
          </div>
          
          {/* Country List */}
          <div className="overflow-y-auto max-h-48">
            {filteredCountries.map(country => (
              <button
                key={country.code}
                type="button"
                onClick={() => handleCountryChange(country)}
                className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-left ${
                  selectedCountry.code === country.code ? 'bg-violet-50' : ''
                }`}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1 text-sm text-gray-700">{country.name}</span>
                <span className="text-sm text-gray-500">{country.dial}</span>
                {selectedCountry.code === country.code && (
                  <Check className="w-4 h-4 text-violet-600" />
                )}
              </button>
            ))}
            {filteredCountries.length === 0 && (
              <p className="text-sm text-gray-500 p-3 text-center">No countries found</p>
            )}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowDropdown(false);
            setSearchTerm("");
          }}
        />
      )}
    </div>
  );
}
